import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useNotification } from "@/composables/notify.ts";
import { usePermissions } from "@/composables/usePermissions.ts";
import appStore from "@/store/app.ts";
import access from "@/router/access.ts";
import app from "@/router/app.ts";
import authStore from "@/store/auth.ts";
import emitenteStore from "@/store/emitente.ts";
import fullscreen from "@/router/fullscreen.ts";
import historyStore from "@/store/history.ts";
import publicRoutes from "@/router/public.ts";
import settingsStore from "@/store/settings.ts";

export const router = createRouter({
  history: createWebHistory(),
  routes: [...access, ...app, ...fullscreen, ...publicRoutes] as RouteRecordRaw[],
  scrollBehavior(to) {
    if (to.hash) {
      return { behavior: "smooth", el: to.hash };
    }
    return { left: 0, top: 0 };
  },
});

const { hasPermission } = usePermissions();
const { notify } = useNotification();

router.beforeEach(async (to, from) => {
  await appStore.ensureIsMounted();
  historyStore.state.from = from.fullPath;

  if (to.path === "/" && authStore.isAuthenticated()) {
    return { name: "home" };
  }

  const isPublic = to.matched.some((record) => record.meta.isPublic);
  if (isPublic) return true;

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresEmitente = to.matched.some((record) => record.meta.requiresEmitente);
  const requiresGuest = to.matched.some((record) => record.meta.requiresGuest);

  if (!(requiresAuth || requiresGuest)) {
    notify({ text: `Essa rota não foi configurada, contate o suporte: ${to.fullPath}`, type: "error" });
    return false;
  }

  if (requiresGuest) {
    return authStore.isGuest() ? true : { name: "home" };
  }

  if (authStore.isGuest()) {
    return { name: "login", query: to.query };
  }

  // Checar sessionStorage para bloqueio por cancelamento (resolve F5)
  if (!appStore.state.sistemaBloqueado) {
    try {
      const raw = sessionStorage.getItem("cancelamento_bloqueado");
      if (raw) {
        const { blocked, ts } = JSON.parse(raw) as { blocked: boolean; ts: number };
        if (blocked && Date.now() - ts <= 60_000) {
          appStore.state.sistemaBloqueado = true;
        }
      }
    } catch {}
  }

  if (appStore.state.sistemaBloqueado && to.name !== "bloqueado" && !authStore.state.current_user?.is_admin) {
    return { name: "bloqueado" };
  }

  await authStore.ensureIsFetched();

  const { current_user } = authStore.state;
  if (to.name === "configuracoes.atualizacao-cnpj" && !current_user.is_admin) {
    notify({ text: "Acesso restrito a administradores", type: "warn" });
    return { name: "home" };
  }
  if (!current_user.is_admin && current_user.permissions.length === 0) {
    return to.name === "sem-acesso" ? true : { name: "sem-acesso" };
  }

  if (
    !current_user.is_admin &&
    settingsStore.getSetting("implantacao_onboarding_enabled") &&
    settingsStore.getSetting("onboarding_contrato_obrigatorio") &&
    !settingsStore.getSetting("onboarding_signed_client")
  ) {
    window.location.assign("/onboarding/setup");
    return false;
  }

  if (requiresEmitente && emitenteStore.isUnset()) {
    return { name: "emitentes" };
  }

  if (!to.name) {
    throw new Error(`Rota sem nome não pode ser verificada quanto a permissões: ${to.fullPath}`);
  }

  if (to.meta?.skipPermissionCheck) {
    return true;
  }

  const permissionKey = (to.meta?.permission as string | string[] | undefined) ?? (to.name as string);
  const userHasPermission = hasPermission(permissionKey);

  if (!userHasPermission) {
    notify({ text: "Você não tem permissão para acessar esta página", type: "warn" });
    return { name: "home" };
  }

  return true;
});
